import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { get } from '../api';
import { ClockIcon, InvoiceIcon, WarningIcon } from '../components/DashboardIcons';
import { AssignmentsIcon } from '../components/NavIcons';
import { addDays, currentLocale, mondayOf } from '../dates';
import { useLoad } from '../hooks';
import { ToSendCard } from './ToSend';

interface Today {
  id: number;
  date: string;
  start_time: string;
  customer_name: string;
  locality: string;
  lead_name: string;
  state: string;
  reason: string;
  flags: string[];
}
interface AssignmentRow {
  id: number;
  customer_name: string;
  status_note?: string | null;
}
interface Dash {
  today: string;
  attention: {
    no_lead: number;
    unconfirmed: number;
    clash: number;
    time_off?: number;
    changed: number;
    needing_a_look: number;
    first_needing_a_look?: string | null;
  };
  visits_today: Today[];
  unassigned_count: number;
  needing_a_look?: { id: number; date: string; start_time: string; customer_name: string; flags: string[] }[];
  draft_count: number;
  draft: AssignmentRow[];
  on_hold_count: number;
  on_hold: AssignmentRow[];
  ready_to_invoice: { client_id: number; client_name: string; visits: number; amount: string }[];
}

/** The full date and the Monday–Sunday range it falls in, for the page subtitle. */
function subtitleFor(today: string) {
  const full = new Date(`${today}T00:00:00Z`).toLocaleDateString(currentLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const monday = mondayOf(today);
  const sunday = addDays(monday, 6);
  const end = new Date(`${sunday}T00:00:00Z`).toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return `${full} · week of ${Number(monday.slice(8))} – ${end}`;
}

/** Why a visit is on the "needing a look" list, in the office's words. */
const LOOK_REASON: Record<string, string> = {
  no_lead: 'no lead',
  time_off: 'time off',
  clash: 'clash',
  unconfirmed: 'not confirmed',
};

/** "Thu 8 Oct, 09:00 · no lead, clash" */
function lookSub(v: { date: string; start_time: string; flags: string[] }) {
  const day = new Date(`${v.date}T00:00:00Z`).toLocaleDateString(currentLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const why = v.flags.map((f) => LOOK_REASON[f] ?? f).join(', ');
  return `${day}, ${v.start_time} · ${why}`;
}

/**
 * One thing the office might need to act on: an icon, a count badge, a short
 * list of what makes it up (or an all-clear line when there's nothing), and a
 * link to where it's dealt with.
 */
function AttentionCard(props: {
  icon: ReactNode;
  title: string;
  hint?: string;
  count: number;
  items?: { id: number; to: string; label: string; sub?: string }[];
  emptyText: string;
  footLink: { to: string; label: string };
  /** A button beside the foot link for starting a new one straight away. */
  footAction?: { to: string; label: string };
}) {
  const items = props.items ?? [];
  return (
    <section className="box-list" aria-labelledby={`card-${props.title}`}>
      <h2 id={`card-${props.title}`}>
        <span className="card-title">
          {props.icon} {props.title}
        </span>
        <span className={props.count > 0 ? 'count-badge on' : 'count-badge'}>{props.count}</span>
      </h2>
      {props.hint && <p className="card-hint muted">{props.hint}</p>}
      {items.length === 0 && <p className="empty">{props.emptyText}</p>}
      {items.map((it) => (
        <Link key={it.id} to={it.to} className="box-item">
          <span>
            <strong>{it.label}</strong>
            {it.sub && <span className="muted sub"> {it.sub}</span>}
          </span>
        </Link>
      ))}
      <p className="foot">
        <Link to={props.footLink.to}>{props.footLink.label}</Link>
        {props.footAction && (
          <Link className="button foot-action" to={props.footAction.to}>
            {props.footAction.label}
          </Link>
        )}
      </p>
    </section>
  );
}

export function Dashboard() {
  const dash = useLoad(() => get<Dash>('/api/dashboard'), []);
  const d = dash.data;
  const ready = d?.ready_to_invoice ?? [];
  const readyVisits = ready.reduce((n, r) => n + r.visits, 0);

  return (
    <>
      <h1>Dashboard</h1>
      {d && <p className="muted">{subtitleFor(d.today)}</p>}
      {dash.error && (
        <p role="alert" className="error">
          {dash.error}
        </p>
      )}
      {dash.loading && <p>Loading…</p>}
      {d && (
        <>
          <h2 className="section-h">Needs your attention</h2>
          <div className="attn-grid">
            <ToSendCard />
            <AttentionCard
              icon={<AssignmentsIcon />}
              title="Draft assignments"
              hint="Being set up, no visits yet"
              count={d.draft_count}
              items={(d.draft ?? []).map((a) => ({
                id: a.id,
                to: `/assignments/${a.id}`,
                label: a.customer_name,
              }))}
              emptyText="No assignments are drafts."
              footLink={{ to: '/assignments?status=draft', label: 'All drafts' }}
              footAction={{ to: '/assignments/new', label: '+ Add assignment' }}
            />
            <AttentionCard
              icon={<WarningIcon />}
              title="Visits needing a look"
              hint="From today on: no lead, a contractor on time off, a clash or not confirmed"
              count={d.attention?.needing_a_look ?? 0}
              items={(d.needing_a_look ?? []).map((v) => ({
                id: v.id,
                to: `/visits/${v.id}`,
                label: v.customer_name,
                sub: lookSub(v),
              }))}
              emptyText="Nothing flagged from today on."
              // The week of the earliest one, filtered to what needs attention.
              footLink={{
                to: `/schedule?show=attention${
                  d.attention?.first_needing_a_look
                    ? `&date=${d.attention.first_needing_a_look}`
                    : ''
                }`,
                label:
                  (d.attention?.needing_a_look ?? 0) > (d.needing_a_look?.length ?? 0)
                    ? `See all ${d.attention.needing_a_look} on the schedule`
                    : 'See them on the schedule',
              }}
            />
            <AttentionCard
              icon={<InvoiceIcon />}
              title="Visits to invoice"
              count={readyVisits}
              items={ready.map((r) => ({
                id: r.client_id,
                to: '/money',
                label: r.client_name,
                sub: `${r.visits} ${r.visits === 1 ? 'visit' : 'visits'}`,
              }))}
              emptyText="Nothing waiting to invoice."
              footLink={{ to: '/money', label: 'Create invoices' }}
            />
            <AttentionCard
              icon={<ClockIcon />}
              title="On hold"
              hint="Waiting on something outside the office"
              count={d.on_hold_count}
              items={(d.on_hold ?? []).map((a) => ({
                id: a.id,
                to: `/assignments/${a.id}`,
                label: a.customer_name,
                sub: a.status_note ?? undefined,
              }))}
              emptyText="Nothing is on hold."
              footLink={{ to: '/assignments?status=on_hold', label: 'All on hold' }}
            />
          </div>

          <p className="page-foot">
            <Link to="/settings?tab=status">System status</Link>
          </p>
        </>
      )}
    </>
  );
}
